/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useRef, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";

import { APPLICATION_ROUTES } from "../../../constants";
import { StoreContext } from "../../../contexts";
import PillButton, { VARIANTS } from "../../atoms/PillButton";
import classes from "./MyMaorHeader.module.scss";
import cs from "classnames";
import myMaorLogo from "../../../assets/images/my-maor-new-logo.png";
import maorLogo from "../../../assets/images/maor-sm.png";
import iconVideoLibrary from "../../../assets/images/icon-video-library.png";
import iconColoringPages from "../../../assets/images/icon-coloring-pages.png";
import iconAudioLibrary from "../../../assets/images/icon-audio-library.png";
import iconBirthdays from "../../../assets/images/icon-birthdays.png";
import iconContest from "../../../assets/images/icon-contest.png";
import iconStore from "../../../assets/images/icon-store.png";
import iconLogin from "../../../assets/images/login.webp";
import iconSubscribe from "../../../assets/images/subscribe.webp";
import iconDonate from "../../../assets/images/donate.webp";
import MyMaorModal from "../MyMaorModal";
import PartnersSectionNew from "../PartnersSectionNew/PartnersSectionNew";
import DonateContent from "../DonateModal/DonateContent";
import PromotionalBanner from "../PromotionalBanner";

const leftNavItems = [
  {
    icon: iconVideoLibrary,
    label: "Video Library",
    to: APPLICATION_ROUTES.MY_MAOR_MAIN,
    internal: true,
    imgClass: classes.videoLibraryImg,
  },
  {
    icon: iconColoringPages,
    label: "Coloring Pages",
    to: APPLICATION_ROUTES.COLORING_PAGES,
    internal: true,
    imgClass: classes.coloringPagesImg,
  },
  {
    icon: iconAudioLibrary,
    label: "Audio Library",
    to: APPLICATION_ROUTES.AUDIO_LIBRARY,
    internal: true,
    imgClass: classes.audioLibraryImg,
  },
];

const rightNavItems = [
  {
    icon: iconBirthdays,
    label: "Birthdays",
    to: APPLICATION_ROUTES.BIRTHDAY_PACKAGES_PAGE,
    internal: true,
    iconScale: 1.25,
    imgClass: classes.birthdaysImg,
  },
  {
    icon: iconContest,
    label: "Contest",
    to: APPLICATION_ROUTES.WRITINNG_COMPETITION,
    internal: true,
    imgClass: classes.contestImg,
  },
  {
    icon: iconStore,
    label: "Store",
    to: "https://maor.shop/",
    internal: false,
    imgClass: classes.storeImg,
  },
];

const NavItem = ({ icon, label, to, internal, onClick, iconScale, imgClass }) => {
  const iconStyle = iconScale ? { transform: `scale(${iconScale})` } : undefined;
  const content = (
    <>
      <div className={classes.navIconWrap}>
        <img src={icon} alt={label} className={cs(classes.navIcon, imgClass)} style={iconStyle} />
      </div>
      <span className={classes.navLabel}>{label}</span>
    </>
  );

  if (onClick) {
    return (
      <button onClick={onClick} className={classes.navItem}>
        {content}
      </button>
    );
  }

  if (!internal) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.navItem}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      to={to}
      exact
      className={classes.navItem}
      activeClassName={classes.navItemActive}
    >
      {content}
    </NavLink>
  );
};

const allNavItems = [...leftNavItems, ...rightNavItems];

const MyMaorHeader = observer(() => {
  const history = useHistory();
  const { t } = useTranslation();
  const { authorization, common } = useContext(StoreContext);
  const [donateModalOpen, setDonateModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const mobileRef = useRef(null);
  const accountRef = useRef(null);

  const isLoggedIn = authorization.isAuthenticated;
  const membership = isLoggedIn ? authorization.membership : null;
  // Plans that come with birthday credit (Birthday, Premium) show the balance instead of the plan name
  const planLabel = !membership
    ? null
    : membership.CREDITS > 0
      ? `$${Number(authorization.user.maor_credit || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} Credit`
      : t(membership.TITLE_TRANSLATION_KEY);
  const lastName = authorization.lastName || "";
  const familyName = lastName
    ? `${lastName.charAt(0).toUpperCase()}${lastName.slice(1)}`
    : "My Account";

  const handleSignIn = () => history.push(APPLICATION_ROUTES.SIGN_IN);
  const handleSignOut = () => authorization.signOut();
  const handleSubscribe = () => common.set("isSubscriptionModalOpen", true);
  const handleDonate = () => setDonateModalOpen(true);
  const handleMaorWebsite = () => history.push(APPLICATION_ROUTES.HOME_PAGE);
  const closeMenu = () => setMenuOpen(false);

  // Lets the restriction popup's "Become a Partner Today!" open the subscription packages
  useEffect(() => {
    common.set("hasSubscriptionModal", true);
    return () => {
      common.set("hasSubscriptionModal", false);
      common.set("isSubscriptionModalOpen", false);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e) => {
      if (mobileRef.current && !mobileRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (!accountOpen) return;
    const onClickOutside = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    const onKeyDown = (e) => e.key === "Escape" && setAccountOpen(false);
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [accountOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <PromotionalBanner />
      {/* ── Desktop header ── */}
      <div className={classes.container}>
        <div className={classes.inner}>
          <div className={classes.leftActions}>
            <PillButton
              icon={<img src={maorLogo} alt="Maor" className={classes.maorLogo} />}
              onClick={handleMaorWebsite}
              variant={VARIANTS.YELLOW}
              justifyContent="center"
              gap="8px"
              minWidth="110px"
            >
              Website
            </PillButton>
          </div>

          <nav className={classes.nav}>
            <div className={classes.navGroup}>
              {leftNavItems.map((item) => (
                <NavItem key={item.label} {...item} />
              ))}
            </div>

            <NavLink
              to={APPLICATION_ROUTES.MY_MAOR_MAIN}
              className={classes.centerLogo}
            >
              <img src={myMaorLogo} alt="My Maor" className={classes.centerLogoImg} />
            </NavLink>

            <div className={classes.navGroup}>
              {rightNavItems.map((item) => (
                <NavItem key={item.label} {...item} />
              ))}
            </div>
          </nav>

          <div className={classes.rightActions}>
            {!isLoggedIn ? (
              <PillButton
                onClick={handleSignIn}
                icon={<img src={iconLogin} alt="Log In" className={classes.actionIcon} />}
              >
                Log In
              </PillButton>
            ) : (
              // Opens on hover, and on click/tap for touch screens and keyboards
              <div
                ref={accountRef}
                className={classes.accountMenu}
                onMouseEnter={() => setAccountOpen(true)}
                onMouseLeave={() => setAccountOpen(false)}
              >
                <PillButton
                  onClick={() => setAccountOpen(true)}
                  icon={<img src={iconLogin} alt="Account" className={classes.actionIcon} />}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                >
                  <span className={classes.familyName}>{familyName}</span>
                </PillButton>
                {accountOpen && (
                  <div className={classes.accountDropdown} role="menu">
                    <button
                      role="menuitem"
                      className={classes.accountDropdownItem}
                      onClick={() => { setAccountOpen(false); handleSignOut(); }}
                    >
                      <img src={iconLogin} alt="" className={classes.actionIcon} />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            )}
            <PillButton
              variant={VARIANTS.YELLOW}
              onClick={handleSubscribe}
              icon={<img src={iconSubscribe} alt="Subscribe" className={classes.actionIcon} />}
            >
              {planLabel || "Subscribe"}
            </PillButton>
            <PillButton
              variant={VARIANTS.DEEP_ORANGE}
              onClick={handleDonate}
              icon={<img src={iconDonate} alt="Donate" className={classes.actionIcon} />}
            >
              Donate
            </PillButton>
          </div>
        </div>
      </div>

      {/* Blur backdrop — click outside to close */}
      <div
        className={cs(classes.blurBackdrop, menuOpen && classes.blurBackdropVisible)}
        onClick={closeMenu}
      />

      {/* ── Mobile header ── */}
      <div ref={mobileRef} className={cs(classes.mobileContainer, menuOpen && classes.mobileContainerOpen)}>
        {/* Top bar */}
        <div className={classes.mobileBar}>
          <PillButton
            variant={VARIANTS.YELLOW}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            icon={
              <div className={classes.toggleIconWrap}>
                <div className={cs(classes.burgerIcon, menuOpen && classes.iconFadeOut)}>
                  <span /><span /><span />
                </div>
                <div className={cs(classes.closeIcon, !menuOpen && classes.iconFadeOut)}>
                  ✕
                </div>
              </div>
            }
            style={{ minWidth: "auto", padding: "6px 10px" }}
          />

          <NavLink
            to={APPLICATION_ROUTES.MY_MAOR_MAIN}
            className={classes.mobileCenterLogo}
            onClick={closeMenu}
          >
            <img src={myMaorLogo} alt="My Maor" className={classes.mobileCenterLogoImg} />
          </NavLink>

          <div className={classes.mobileActionBtns}>
            {!isLoggedIn ? (
              <PillButton
                icon={<img src={iconLogin} alt="Log In" className={classes.actionIcon} />}
                onClick={handleSignIn}
                style={{ minWidth: "auto" }}
              />
            ) : (
              <PillButton
                icon={<img src={iconLogin} alt="Log Out" className={classes.actionIcon} />}
                onClick={handleSignOut}
                style={{ minWidth: "auto" }}
              />
            )}
            <PillButton
              variant={VARIANTS.YELLOW}
              icon={<img src={iconSubscribe} alt="Subscribe" className={classes.actionIcon} />}
              onClick={handleSubscribe}
              style={{ minWidth: "auto" }}
            />
            <PillButton
              variant={VARIANTS.DEEP_ORANGE}
              icon={<img src={iconDonate} alt="Donate" className={classes.actionIcon} />}
              onClick={handleDonate}
              style={{ minWidth: "auto" }}
            />
          </div>
        </div>

        {/* Expanded menu — always mounted, animated via CSS max-height */}
        <div className={cs(classes.mobileMenu, menuOpen && classes.mobileMenuOpen)}>
          <div className={classes.mobileNavGrid}>
            {allNavItems.map((item) =>
              item.internal ? (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={classes.mobileNavItem}
                  onClick={closeMenu}
                >
                  <div className={classes.mobileNavIconWrap}>
                    <img src={item.icon} alt={item.label} className={cs(classes.mobileNavIcon, item.imgClass)} />
                  </div>
                  <span className={classes.mobileNavLabel}>{item.label}</span>
                </NavLink>
              ) : (
                <a
                  key={item.label}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.mobileNavItem}
                  onClick={closeMenu}
                >
                  <div className={classes.mobileNavIconWrap}>
                    <img src={item.icon} alt={item.label} className={cs(classes.mobileNavIcon, item.imgClass)} />
                  </div>
                  <span className={classes.mobileNavLabel}>{item.label}</span>
                </a>
              )
            )}
          </div>

          <button
            className={classes.mobileWebsiteBtn}
            onClick={() => { handleMaorWebsite(); closeMenu(); }}
          >
            Go to general
            <img src={maorLogo} alt="Maor" className={classes.inlineLogoImg} />
            website page
          </button>
        </div>
      </div>

      <MyMaorModal
        isOpen={common.isSubscriptionModalOpen}
        onClose={() => common.set("isSubscriptionModalOpen", false)}
        title="Subscription Packages"
      >
        <div className={classes.modalPackagesWrapper}>
          <PartnersSectionNew noWrapper />
        </div>
      </MyMaorModal>

      <MyMaorModal
        isOpen={donateModalOpen}
        onClose={() => setDonateModalOpen(false)}
        title="Donate to Maor"
      >
        <DonateContent onSubmit={() => setDonateModalOpen(false)} />
      </MyMaorModal>
    </>
  );
});

export default MyMaorHeader;
